import React, { createContext, useState as useReactState, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import { DatabaseProvider } from './context/DatabaseContext';
import { useAuth } from './context/AuthContext';
import AuthPage from './pages/AuthPage';
import DashboardLayout from './components/Layout/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import DatabasePage from './pages/DatabasePage';
import ArticleDetailPage from './pages/ArticleDetailPage';
import LandingPage from './pages/LandingPage';
import PricingPage from './pages/PricingPage';
import { ToastManager } from './components/ToastManager';
import './App.css';
import SettingsPage from './pages/SettingsPage';
import ConferencesPage from './pages/ConferencesPage';
import PatentsPage from './pages/PatentsPage';
import FavoritesPage from './pages/FavoritesPage';
import HistoryPage from './pages/HistoryPage';
import TestPage from './pages/TestPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import BillingPage from './pages/BillingPage';

// Ajout d'un contexte pour la synchro temps réel des préférences filtres
export const FilterPreferencesContext = createContext({ preferences: {}, setPreferences: () => {} });

// Composant pour les routes protégées
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  // Pendant la vérification d'authentification, afficher un indicateur de chargement
  if (loading) {
    return <div className="loading-screen">Chargement...</div>;
  }

  // Rediriger vers la page de connexion si non authentifié
  if (!isAuthenticated) {
    return <Navigate to="/login" />;
  }

  return children;
};

const AgendaPage = lazy(() => import('./pages/AgendaPage'));

function App() {
  // Ajout de l'état global pour le mode d'affichage (list/card)
  const [viewMode, setViewMode] = React.useState(() => {
    if (typeof window === 'undefined') {
      return 'list';
    }
    return localStorage.getItem('viewMode') || 'list';
  });

  // Persistance du mode d'affichage
  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('viewMode', viewMode);
    }
  }, [viewMode]);

  const [tagPreference, setTagPreference] = React.useState(() => {
    if (typeof window === 'undefined') {
      return 'with-tags';
    }
    return localStorage.getItem('tagPreference') || 'with-tags';
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('tagPreference', tagPreference);
    }
  }, [tagPreference]);

  const [tagStylePreference, setTagStylePreference] = React.useState(() => {
    if (typeof window === 'undefined') {
      return 'colored';
    }
    return localStorage.getItem('tagStylePreference') || 'colored';
  });

  React.useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('tagStylePreference', tagStylePreference);
    }
  }, [tagStylePreference]);

  const [filterPreferences, setFilterPreferences] = useReactState(() => {
    try {
      return JSON.parse(localStorage.getItem('filterPreferences')) || {};
    } catch {
      return {};
    }
  });

  return (
    <AuthProvider>
      <DatabaseProvider>
        <FilterPreferencesContext.Provider value={{ preferences: filterPreferences, setPreferences: setFilterPreferences }}>
          <Router>
            <AppRoutes
              filterPreferences={filterPreferences}
              setFilterPreferences={setFilterPreferences}
              viewMode={viewMode}
              setViewMode={setViewMode}
              tagPreference={tagPreference}
              setTagPreference={setTagPreference}
              tagStylePreference={tagStylePreference}
              setTagStylePreference={setTagStylePreference}
            />
            <ToastManager />
          </Router>
        </FilterPreferencesContext.Provider>
      </DatabaseProvider>
    </AuthProvider>
  );
}

export function AppRoutes({ filterPreferences, setFilterPreferences, viewMode, setViewMode, tagPreference, setTagPreference, tagStylePreference, setTagStylePreference }) {
  return (
    <Suspense fallback={<div>Chargement...</div>}>
      <Routes>
        {/* Rediriger la racine vers /accueil */}
        <Route path="/" element={<Navigate to="/login" />} />
        <Route path="/login" element={<AuthPage />} />
        <Route path="/register" element={<AuthPage />} />

        {/* Email Verification & Password Reset Routes */}
        <Route path="/verify-email/:token" element={<VerifyEmailPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />

        {/* Public Pricing Page */}
        <Route path="/pricing" element={<PricingPage />} />

        <Route path="/article" element={
          <ProtectedRoute>
            <ArticleDetailPage />
          </ProtectedRoute>
        } />

        {/* Dashboard Routes */}
        <Route path="/dashboard" element={
          <ProtectedRoute>
            <DashboardLayout
              tagPreference={tagPreference}
              onTagPreferenceChange={setTagPreference}
              tagStylePreference={tagStylePreference}
              onTagStylePreferenceChange={setTagStylePreference}
            />
          </ProtectedRoute>
        }>
          <Route index element={<DashboardHome />} />
          <Route path="database" element={<DatabasePage viewMode={viewMode} onViewModeChange={setViewMode} tagPreference={tagPreference} tagStylePreference={tagStylePreference} />}
  />
          <Route path="test" element={<TestPage viewMode={viewMode} tagPreference={tagPreference} />} />
          <Route path="conferences" element={<ConferencesPage viewMode={viewMode} onViewModeChange={setViewMode} tagPreference={tagPreference} />} />
          <Route path="patents" element={<PatentsPage viewMode={viewMode} onViewModeChange={setViewMode} tagPreference={tagPreference} />} />
          <Route path="favorites" element={<FavoritesPage />} />
          <Route path="history" element={<HistoryPage />} />
          <Route path="settings" element={<SettingsPage onPreferencesChange={setFilterPreferences} />} />
          <Route path="billing" element={<BillingPage />} />
          <Route path="agenda" element={<AgendaPage />} />
        </Route>

        <Route path="*" element={<Navigate to="/login" />} />
      </Routes>
    </Suspense>
  );
}

export default App;